import React, { useState } from "react";
import { FaChevronRight, FaChevronDown, FaTrash, FaPlus } from "react-icons/fa";
import styles from "./ConditionEditor.module.scss";
import _ from "../../../../i18n";

const ConditionEditor = ({
  condition,
  index,
  ruleData,
  setRuleData,
  parentIndex = null,
  isSubCondition = false,
  variables = [],
  operators = [],
  logicalOperators = [],
  updateCondition,
  removeCondition,
  addCondition,
  selected,
  setSelected,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const handleConditionTypeChange = (newType) => {
    let updatedCondition = { ...condition, type: newType };

    if (newType === "range") {
      updatedCondition = {
        ...updatedCondition,
        minValue: "",
        maxValue: "",
        minOperator: "<",
        maxOperator: "<=",
      };
    } else if (newType === "nested") {
      updatedCondition = {
        ...updatedCondition,
        subConditions: condition.subConditions || [
          {
            type: "simple",
            variable: "realvsBilled",
            operator: ">",
            value: "",
          },
        ],
      };
    } else {
      updatedCondition = {
        type: "simple",
        variable: condition.variable || "realvsBilled",
        operator: condition.operator || ">",
        value: condition.value || "",
      };
    }

    if (parentIndex !== null) {
      const newRuleData = { ...ruleData };
      newRuleData.conditions[parentIndex].subConditions[index] =
        updatedCondition;
      setRuleData(newRuleData);
    } else {
      const newRuleData = { ...ruleData };
      newRuleData.conditions[index] = updatedCondition;
      setRuleData(newRuleData);
    }
  };

  return (
    <div
      className={`${styles.conditionEditor} ${
        isSubCondition ? styles.isSubCondition : ""
      }`}
      style={{
        marginLeft: condition.groupId != undefined ? "30px" : 0,
        border: condition.groupId != undefined ? `1px solid ${condition.color}` : "",
        backgroundColor:
          condition.groupId != undefined ? `${condition.color}20` : "transparent",
      }}
    >
      <input
        type="checkbox"
        className={styles.checkbox}
        checked={selected.includes(index)}
        onChange={() => {
          setSelected((prev) =>
            prev.includes(index)
              ? prev.filter((i) => i !== index)
              : [...prev, index]
          );
        }}
      />
      <div className={styles.controlsWrapper}>
        <select
          value={condition.type || "simple"}
          onChange={(e) => handleConditionTypeChange(e.target.value)}
          className={styles.inputField}
          style={{ width: "120px" }}
        >
          <option value="simple">Simple</option>
          <option value="range">Plage</option>
          <option value="nested">Impriqué</option>
        </select>

        {condition.type === "nested" && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={styles.collapseButton}
            aria-label={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? (
              <FaChevronRight size={16} />
            ) : (
              <FaChevronDown size={16} />
            )}
          </button>
        )}

        {condition.type !== "nested" && (
          <select
            value={condition.variable}
            onChange={(e) =>
              updateCondition(
                ruleData,
                setRuleData,
                index,
                "variable",
                e.target.value,
                parentIndex
              )
            }
            className={styles.inputField}
          >
            {variables.map((variable) => (
              <option key={variable.value} value={variable.value}>
                {variable.acronym}
              </option>
            ))}
          </select>
        )}

        {condition.type === "range" ? (
          <>
            <input
              type="number"
              value={condition.minValue || ""}
              onChange={(e) =>
                updateCondition(
                  ruleData,
                  setRuleData,
                  index,
                  "minValue",
                  e.target.value,
                  parentIndex
                )
              }
              className={styles.numberInput}
              placeholder="Min"
            />
            <span className={styles.unit}>%</span>

            <select
              value={condition.minOperator || "<"}
              onChange={(e) =>
                updateCondition(
                  ruleData,
                  setRuleData,
                  index,
                  "minOperator",
                  e.target.value,
                  parentIndex
                )
              }
              className={styles.inputField}
            >
              <option value="<">&lt;</option>
              <option value="<=">&le;</option>
            </select>

            <span className={styles.variableAcronym}>
              {variables.find((v) => v.value === condition.variable)?.acronym}
            </span>

            <select
              value={condition.maxOperator || "<="}
              onChange={(e) =>
                updateCondition(
                  ruleData,
                  setRuleData,
                  index,
                  "maxOperator",
                  e.target.value,
                  parentIndex
                )
              }
              className={styles.inputField}
            >
              <option value="<">&lt;</option>
              <option value="<=">&le;</option>
            </select>

            <input
              type="number"
              value={condition.maxValue || ""}
              onChange={(e) =>
                updateCondition(
                  ruleData,
                  setRuleData,
                  index,
                  "maxValue",
                  e.target.value,
                  parentIndex
                )
              }
              className={styles.numberInput}
              placeholder="Max"
            />
            <span className={styles.unit}>%</span>
          </>
        ) : condition.type === "simple" ? (
          <>
            <select
              value={condition.operator}
              onChange={(e) =>
                updateCondition(
                  ruleData,
                  setRuleData,
                  index,
                  "operator",
                  e.target.value,
                  parentIndex
                )
              }
              className={styles.inputField}
            >
              {operators.map((operator) => (
                <option key={operator.value} value={operator.value}>
                  {operator.label}
                </option>
              ))}
            </select>

            <input
              type="number"
              value={condition.value}
              onChange={(e) =>
                updateCondition(
                  ruleData,
                  setRuleData,
                  index,
                  "value",
                  e.target.value,
                  parentIndex
                )
              }
              className={styles.numberInput}
              placeholder={_("value")}
            />
            <span className={styles.unit}>%</span>
          </>
        ) : null}

        <button
          onClick={() =>
            removeCondition(ruleData, setRuleData, index, parentIndex)
          }
          className={styles.removeButton}
          aria-label="Remove condition"
        >
          <FaTrash size={14} />
        </button>
      </div>

      {/* Nested conditions */}
      {condition.type === "nested" && !collapsed && (
        <div className={styles.nestedConditions}>
          {condition.subConditions?.map((subCondition, subIndex) => (
            <div key={subIndex}>
              {subIndex > 0 && (
                <div
                  className={styles.logicalOperatorWrapper}
                  style={{
                    marginLeft:
                      subCondition.groupId != undefined && subIndex > 1
                        ? "30px"
                        : 0,
                  }}
                >
                  <select
                    value={subCondition.logicalOp || "&&"}
                    onChange={(e) =>
                      updateCondition(
                        ruleData,
                        setRuleData,
                        subIndex,
                        "logicalOp",
                        e.target.value,
                        index
                      )
                    }
                    className={styles.inputField}
                  >
                    {logicalOperators.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {/* Recursive call to render sub-conditions */}
              <ConditionEditor
                condition={subCondition}
                index={subIndex}
                parentIndex={index}
                isSubCondition={true}
                ruleData={ruleData}
                setRuleData={setRuleData}
                variables={variables}
                operators={operators}
                logicalOperators={logicalOperators}
                updateCondition={updateCondition}
                removeCondition={removeCondition}
                addCondition={addCondition}
                selected={selected}
                setSelected={setSelected}
              />
            </div>
          ))}

          <button
            onClick={() => addCondition(ruleData, setRuleData, index)}
            className={styles.addSubConditionButton}
          >
            <FaPlus size={14} />
            <span>Ajouter sous-condition</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ConditionEditor;
